import { explicitCitationPageFromMedia, junkdrawerDocumentApiPathFromUrl } from '@/shared/functions';
import { storageUrlForDisplay } from '@/shared/storageUrlForDisplay';
import {
  resolveCompactPreview,
  resolveCompactPreviewRotation,
} from '@/shared/timelineThumbnails';
import type { MediaSource } from '@/shared/types';

/** Document types whose mini viewer pages through a controlled `page` prop. */
export const HORIZONTAL_TIMELINE_PAGEABLE_TYPES = new Set(['pdf', 'docx', 'pptx']);

export function cardPageNumber(
  pages: Readonly<Record<string, number>>,
  slideId: string | undefined,
  citationPage: number,
): number {
  if (!slideId) return citationPage;
  const stored = pages[slideId];
  return typeof stored === 'number' && stored > 0 ? stored : citationPage;
}

/**
 * Page the card itself has already chosen.
 *
 * Empty card state must not become a controlled page. The citation fallback
 * is what ``cardPageNumber`` returns for the theme viewer, and passing it
 * into ``PdfViewer`` makes that viewer persist the citation over the page
 * remembered for the event.
 */
export function cardControlledViewingPage(
  pages: Readonly<Record<string, number>>,
  slideId: string | undefined,
): number | undefined {
  if (!slideId) return undefined;
  const stored = pages[slideId];
  return typeof stored === 'number' && stored > 0 ? stored : undefined;
}

function positivePage(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value >= 1
    ? Math.floor(value)
    : undefined;
}

/**
 * Page a saved document still on this row was rasterized for.
 *
 * ``canvas.pageNumber`` is that page when the thumbnail recorded it. Otherwise
 * the still is the citation page, and a document with no citation was captured
 * from page 1.
 */
export function cardDocumentStillPage(
  media: MediaSource,
  mediaType?: string,
): number | undefined {
  const type = (mediaType || media.mediaType || '').toLowerCase();
  if (!HORIZONTAL_TIMELINE_PAGEABLE_TYPES.has(type)) return undefined;
  const canvasPage = positivePage(media.mediaContext?.canvas?.pageNumber);
  if (canvasPage) return canvasPage;
  return explicitCitationPageFromMedia(media) ?? 1;
}

/**
 * A saved still stays on the horizontal-timeline card only while it is the
 * page in view. Video and image stills are not document pages.
 */
export function cardStillMatchesViewingPage(
  media: MediaSource,
  viewingPage: number | undefined,
  mediaType?: string,
): boolean {
  if (viewingPage == null || viewingPage < 1) return true;
  const stillPage = cardDocumentStillPage(media, mediaType);
  if (stillPage == null) return true;
  return stillPage === viewingPage;
}

/**
 * Per-slide file URL for the mini PDF viewer.
 *
 * The fragment is not sent on the request. It keeps two cards of one PDF from
 * sharing a pdf.js document, so paging one card cannot repaint the other.
 */
export function cardPdfViewerSrc(src: string, slideId: string | undefined): string {
  if (!src || !slideId) return src;
  if (/^(blob:|data:)/i.test(src)) return src;
  const marker = `tplSlide=${encodeURIComponent(slideId)}`;
  const hashIndex = src.indexOf('#');
  if (hashIndex === -1) return `${src}#${marker}`;
  const base = src.slice(0, hashIndex);
  const hash = src.slice(hashIndex + 1);
  if (hash.split('&').includes(marker) || hash.split('&').some((part) => part.startsWith('tplSlide='))) {
    return src;
  }
  return `${base}#${hash}${hash ? '&' : ''}${marker}`;
}

/**
 * Word and PowerPoint cards cannot be painted by the theme PDF viewer.
 * That viewer expects PDF bytes. A raw ``.docx`` / ``.pptx`` is a zip, so
 * pdf.js reports "Invalid PDF structure." An already converted PDF URL
 * (``docx_processing`` / ``.pdf``) can stay on the theme viewer.
 */
export function cardDocumentNeedsConversion(
  mediaItem: MediaSource,
  mediaType?: string,
): boolean {
  const type = (mediaType || mediaItem.mediaType || '').toLowerCase();
  if (type !== 'pdf' && type !== 'docx' && type !== 'pptx') return false;
  const raw = (mediaItem.srcToUse || mediaItem.mediaUrl || mediaItem.mediaSrc || '').trim();
  if (!raw) return false;
  const fallback =
    type === 'pptx' ? 'ms-powerpoint' : type === 'docx' ? 'ms-word' : 'pdf';
  return junkdrawerDocumentApiPathFromUrl(raw, fallback) !== 'pdf';
}

export function rememberCardPage(
  pages: Record<string, number>,
  slideId: string,
  page: number,
): Record<string, number> {
  if (pages[slideId] === page) return pages;
  return { ...pages, [slideId]: page };
}

export type HorizontalTimelineCardPreview =
  | { kind: 'capture'; src: string }
  | { kind: 'source'; src: string; rotation: number };

/**
 * Card media for the horizontal timeline.
 *
 * A saved capture, still frame, or PDF canvas on this row paints as an image
 * while it is the page in view. A page chosen in the event pop-up replaces
 * that still with the source file so the card shows the page the event uses.
 * Shared `sourceContext` captures are ignored so one event's crop cannot
 * appear on every card that cites the same file.
 */
export function resolveHorizontalTimelineCardPreview(
  mediaItem: MediaSource,
  mediaType?: string,
  viewingPage?: number,
): HorizontalTimelineCardPreview {
  const preview = resolveCompactPreview(mediaItem, { ownCaptureOnly: true });
  if (
    preview.kind === 'derived-still' &&
    preview.url &&
    cardStillMatchesViewingPage(mediaItem, viewingPage, mediaType)
  ) {
    return { kind: 'capture', src: preview.url };
  }
  if (mediaType === 'image') {
    const raw = (mediaItem.srcToUse || mediaItem.mediaSrc || mediaItem.mediaUrl || '').trim();
    return {
      kind: 'source',
      src: preview.url || storageUrlForDisplay(raw),
      rotation: resolveCompactPreviewRotation(mediaItem, { ownCaptureOnly: true }),
    };
  }
  const raw = (mediaItem.srcToUse || mediaItem.mediaUrl || mediaItem.mediaSrc || '').trim();
  return { kind: 'source', src: storageUrlForDisplay(raw), rotation: 0 };
}
